/**
 * CORE-748 — how the platform calendar assembles its Live-сессия (room) set.
 *
 * Pure, no RN deps, so it's unit-testable in the node jest env — same reason
 * `calendarSpan.ts` lives outside hooks/useLivesCalendar.ts.
 *
 * The calendar used to source rooms from `getMyRooms` alone, i.e. «rooms I
 * host, joined or subscribed to». That silently made RSVP a *precondition of
 * discovery*: a master published a public Live-сессия, saw it in their own
 * calendar, and every other user — signed in or not — saw nothing until they
 * reached the room by a direct link and pressed «Присоединиться». Discovery
 * now comes from the platform-wide public list; my-rooms is kept only because
 * it carries the caller-aware fields (isSubscribed, own private/subscriber
 * sessions) the anonymous list can't have.
 */
import type { RoomResponse } from '../types/auth';

/**
 * A room falls inside the calendar's loaded month window. Unscheduled rooms
 * (quick/instant, no `scheduledAtUtc`) have no day to sit on and are dropped.
 */
export const isRoomInWindow = (room: RoomResponse, fromMs: number, toMs: number): boolean => {
  if (!room.scheduledAtUtc) return false;
  const t = new Date(room.scheduledAtUtc).getTime();
  return Number.isFinite(t) && t >= fromMs && t <= toMs;
};

/**
 * The month's rooms: mine ∪ public, clipped to the window and deduped by id.
 *
 * Order matters — `myRooms` goes first so that when the same room appears in
 * both lists the caller-aware copy wins. The public listing is fetched without
 * identity, so its `isSubscribed` is always false; taking it over my-rooms'
 * would clear the «Я иду» state of a session the user has already joined.
 */
export const mergeCalendarRooms = (
  myRooms: RoomResponse[],
  publicRooms: RoomResponse[],
  fromMs: number,
  toMs: number,
): RoomResponse[] => {
  const seen = new Set<number>();
  return [...myRooms, ...publicRooms].filter(room => {
    if (!isRoomInWindow(room, fromMs, toMs)) return false;
    if (seen.has(room.id)) return false;
    seen.add(room.id);
    return true;
  });
};

/**
 * CORE-1290 — «завершена ли эта карточка афиши» и «идёт ли она сейчас».
 *
 * Афиша считала это по одним часам: завершено, если прошло `start + duration`.
 * Комната, которую хост закрыл РАНЬШЕ назначенного окна, по часам ещё «не
 * наступила» — и карточка предлагала на неё записаться. Ровно это вернула
 * проверка (комментарий 53423): комната `osG7gW` завершена в 19:10, назначена
 * на 19:45, и после завершения стояла в календаре с активной кнопкой «Я иду».
 *
 * Поэтому терминальный статус с сервера побеждает часы. Правило не новое: так
 * живёт R0 в `computeEffectiveStatus` (`isServerConfirmedEnded`) и панель «Мой
 * календарь» на дашборде (`hasEnded = endTimeMs < now || status === 'Ended'`) —
 * афиша `/lives` осталась единственной поверхностью, до которой оно не доехало.
 *
 * Часы никуда не деваются: они по-прежнему закрывают карточку, которая просто
 * отыграла своё окно, а сервер об этом ещё не сказал.
 */
type CalendarLifecycle = {
  status?: string | null;
  startDateTimeUtc: string;
  durationMinutes?: number | null;
};

const itemWindow = (item: CalendarLifecycle) => {
  const startMs = new Date(item.startDateTimeUtc).getTime();
  return { startMs, endMs: startMs + (item.durationMinutes || 60) * 60_000 };
};

export const isCalendarItemEnded = (item: CalendarLifecycle, nowMs: number): boolean => {
  if (item.status === 'ended') return true;
  if (item.status === 'live') return false;
  const { endMs } = itemWindow(item);
  return Number.isFinite(endMs) && nowMs >= endMs;
};

export const isCalendarItemLive = (item: CalendarLifecycle, nowMs: number): boolean => {
  if (isCalendarItemEnded(item, nowMs)) return false;
  if (item.status === 'live') return true;
  const { startMs, endMs } = itemWindow(item);
  return Number.isFinite(startMs) && nowMs >= startMs && nowMs < endMs;
};

/**
 * Осталась ли карточка в «идёт или ещё впереди» — то, что общий (не «мои
 * события») список показывает на сегодняшний день. Завершённое сюда не
 * попадает даже с будущим временем старта.
 */
export const isCalendarItemStillAhead = (item: CalendarLifecycle, nowMs: number): boolean => {
  if (isCalendarItemEnded(item, nowMs)) return false;
  if (isCalendarItemLive(item, nowMs)) return true;
  const { startMs } = itemWindow(item);
  return Number.isFinite(startMs) && startMs >= nowMs;
};
